import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import platformAPI from "../../../services/platformApi";
import {
  formatRub,
  LabelWithTip,
  METRIC_TIPS,
  MetricCard,
  SaasFunnel,
} from "./PlatformDashboardParts";
import PlatformPageHeader from "./PlatformPageHeader";
import s from "./platformAdmin.module.css";

const BOARD_DOT = {
  new: s.funnelDotNew,
  trial: s.funnelDotTrial,
  activated: s.funnelDotActivated,
  paid: s.funnelDotPaid,
  retained: s.funnelDotRetained,
  risk: s.funnelDotRisk,
  lost: s.funnelDotLost,
};

function pctLabel(v) {
  if (v == null) return "—";
  return `${v}%`;
}

function daysLabel(days) {
  if (days == null) return null;
  if (days < 0) return `${Math.abs(days)} дн. назад`;
  return `${days} дн.`;
}

function ClubFunnelCard({ club }) {
  const left = daysLabel(club.days_left);
  return (
    <Link to={`/admin/tenants/${club.id}`} className={s.funnelClubCard}>
      <strong>{club.name}</strong>
      <small>{club.subdomain}</small>
      <div className={s.funnelClubMeta}>
        <span>{club.plan_name}</span>
        {left ? <span>{left}</span> : null}
      </div>
      <div className={s.funnelClubMeta}>
        <span>{club.clients || 0} сп.</span>
        <span>{club.sessions_30d || 0} зан.</span>
        {club.monthly_price_rub ? (
          <span>{formatRub(club.monthly_price_rub)} ₽</span>
        ) : null}
      </div>
    </Link>
  );
}

export default function PlatformFunnel() {
  const [analytics, setAnalytics] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    platformAPI
      .getAnalytics()
      .then((a) => {
        if (!cancelled) setAnalytics(a.analytics || null);
      })
      .catch((e) => {
        if (!cancelled) {
          setErr(e.response?.data?.error || "Не удалось загрузить воронку");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const funnel = analytics?.funnel || null;
  const forecast = analytics?.forecast || {};
  const board = funnel?.board || { stages: [], columns: {} };

  const q = query.trim().toLowerCase();
  const filteredColumns = useMemo(() => {
    const next = {};
    for (const stage of board.stages || []) {
      const list = board.columns?.[stage.id] || [];
      next[stage.id] = q
        ? list.filter((club) => {
            const hay = `${club.name || ""} ${club.subdomain || ""} ${club.plan_name || ""}`.toLowerCase();
            return hay.includes(q);
          })
        : list;
    }
    return next;
  }, [board, q]);

  return (
    <div className={s.page}>
      <PlatformPageHeader
        title="Воронка"
        lead="Конверсии клубов и доска по стадиям — без дубля сводки."
      />

      {err ? <div className={s.alertError}>{err}</div> : null}
      {loading ? <div className={s.loading}>Загрузка воронки…</div> : null}

      {!loading && analytics ? (
        <>
          <section className={s.kpiStrip}>
            <MetricCard
              label="До оплаты"
              value={pctLabel(funnel?.conversion?.overall_to_paid)}
              hint="Регистрация → оплаченная подписка"
              tip={METRIC_TIPS.saas_funnel}
              accent
            />
            <MetricCard
              label="Trial → оплата"
              value={pctLabel(
                funnel?.conversion?.trial_to_paid ?? forecast.trial_to_paid_rate,
              )}
              hint={
                forecast.expected_paid_from_trials
                  ? `Ожидается ~${forecast.expected_paid_from_trials} оплат`
                  : "По текущим пробным и оплатившим"
              }
              tip={METRIC_TIPS.trial_paid}
            />
            <MetricCard
              label="Удержание"
              value={pctLabel(funnel?.conversion?.retention_rate)}
              hint="Оплатившие, которые проводят занятия"
              tip={METRIC_TIPS.funnel_retained}
            />
            <MetricCard
              label="В риске"
              value={funnel?.at_risk_count ?? 0}
              hint={`Потеряны ${funnel?.lost_count ?? 0} · отключены ${funnel?.suspended_count ?? 0}`}
              tip={METRIC_TIPS.churn_risk}
              warn={(funnel?.at_risk_count || 0) > 0}
            />
          </section>

          <section className={s.chartPanel}>
            <div className={s.panelHeaderRow}>
              <LabelWithTip tip={METRIC_TIPS.saas_funnel} className={s.panelTitle}>
                Накопительные стадии
              </LabelWithTip>
            </div>
            <SaasFunnel funnel={funnel} />
          </section>

          <section className={s.chartPanel}>
            <div className={s.panelHeaderRow}>
              <h3 className={s.panelTitle}>Доска клубов</h3>
              <input
                className={s.funnelSearch}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Клуб, поддомен, тариф"
              />
            </div>
            <p className={s.funnelBoardHint}>
              Каждый клуб в одной колонке. Карточка открывает карточку клуба.
            </p>
            <div className={s.funnelBoard}>
              {(board.stages || []).map((stage) => {
                const list = filteredColumns[stage.id] || [];
                return (
                  <section
                    key={stage.id}
                    className={`${s.funnelCol} ${s[`funnelCol_${stage.id}`] || ""}`}
                  >
                    <header className={s.funnelColHead}>
                      <span className={s.funnelColTitle}>
                        <span
                          className={`${s.funnelDot} ${BOARD_DOT[stage.id] || ""}`}
                        />
                        {stage.label}
                      </span>
                      <span className={s.funnelColCount}>{list.length}</span>
                    </header>
                    <div className={s.funnelColBody}>
                      {list.length ? (
                        list.map((club) => (
                          <ClubFunnelCard key={club.id} club={club} />
                        ))
                      ) : (
                        <div className={s.funnelColEmpty}>Пусто</div>
                      )}
                    </div>
                  </section>
                );
              })}
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
